'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cn } from 'cn';
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
type FieldSetProps = React.ComponentProps<'fieldset'>;
export function FieldSet(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex flex-col gap-6 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <fieldset data-slot="field-set" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldLegendProps = React.ComponentProps<'legend'> & {
  variant?: 'legend' | 'label';
};
export function FieldLegend(props) {
  const $ = _c(10);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      variant: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const variant = t0 === undefined ? "legend" : t0;
  let t1;
  if ($[4] !== className) {
    t1 = cn("mb-3 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest || $[7] !== t1 || $[8] !== variant) {
    t2 = <legend data-slot="field-legend" data-variant={variant} className={t1} {...rest} />;
    $[6] = rest;
    $[7] = t1;
    $[8] = variant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
type FieldGroupProps = React.ComponentProps<'div'>;
export function FieldGroup(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-group" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
const fieldVariants = cva('group/field flex w-full gap-3 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal: 'flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      responsive: 'flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px'
    }
  },
  defaultVariants: {
    orientation: 'vertical'
  }
});
type FieldProps = React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>;
export function Field(props) {
  const $ = _c(11);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      orientation: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const orientation = t0 === undefined ? "vertical" : t0;
  let t1;
  if ($[4] !== className || $[5] !== orientation) {
    t1 = cn(fieldVariants({
      orientation
    }), className);
    $[4] = className;
    $[5] = orientation;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== orientation || $[8] !== rest || $[9] !== t1) {
    t2 = <div role="group" data-slot="field" data-orientation={orientation} className={t1} {...rest} />;
    $[7] = orientation;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type FieldContentProps = React.ComponentProps<'div'>;
export function FieldContent(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-content flex flex-1 flex-col gap-1 leading-snug", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-content" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldLabelProps = React.ComponentProps<typeof Label>;
export function FieldLabel(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50 has-data-checked:border-primary/30 has-data-checked:bg-primary/5 has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-xl has-[>[data-slot=field]]:border *:data-[slot=field]:p-4 dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <Label data-slot="field-label" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldTitleProps = React.ComponentProps<'div'>;
export function FieldTitle(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex w-fit items-center gap-2 text-sm font-medium group-data-[disabled=true]/field:opacity-50", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-label" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldDescriptionProps = React.ComponentProps<'p'>;
export function FieldDescription(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("text-left text-sm/normal font-normal text-muted-foreground group-has-data-horizontal/field:text-balance last:mt-0 nth-last-2:-mt-1 [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary [[data-variant=legend]+&]:-mt-1.5", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <p data-slot="field-description" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldSeparatorProps = React.ComponentProps<'div'> & {
  children?: React.ReactNode;
};
export function FieldSeparator(props) {
  const $ = _c(15);
  let children;
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      children,
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  const hasChildren = Boolean(children);
  let t0;
  if ($[4] !== className) {
    t0 = cn("relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2", className);
    $[4] = className;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Separator className="absolute inset-0 top-1/2" />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== hasChildren) {
    t2 = hasChildren && <span className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground" data-slot="field-separator-content">{children}</span>;
    $[7] = children;
    $[8] = hasChildren;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== hasChildren || $[11] !== rest || $[12] !== t0 || $[13] !== t2) {
    t3 = <div data-slot="field-separator" data-content={hasChildren} className={t0} {...rest}>{t1}{t2}</div>;
    $[10] = hasChildren;
    $[11] = rest;
    $[12] = t0;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
type FieldErrorProps = React.ComponentProps<'div'> & {
  errors?: Array<{
    message?: string;
  } | undefined>;
};
export function FieldError(props) {
  const $ = _c(17);
  let children;
  let className;
  let errors;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      errors,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = errors;
    $[4] = rest;
  } else {
    children = $[1];
    className = $[2];
    errors = $[3];
    rest = $[4];
  }
  let t0;
  bb0: {
    if (children) {
      t0 = children;
      break bb0;
    }
    if (!errors?.length) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[5] !== errors) {
      t1 = new Set(errors.map(_temp).filter(Boolean));
      $[5] = errors;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] !== t1) {
      t2 = [...t1];
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    const uniqueMessages = t2;
    if (uniqueMessages.length === 0) {
      t0 = null;
      break bb0;
    }
    if (uniqueMessages.length === 1) {
      t0 = uniqueMessages[0];
      break bb0;
    }
    let t3;
    if ($[9] !== uniqueMessages) {
      t3 = <ul className="ml-4 flex list-disc flex-col gap-1">{uniqueMessages.map(_temp2)}</ul>;
      $[9] = uniqueMessages;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t0 = t3;
  }
  const content = t0;
  if (!content) {
    return null;
  }
  let t1;
  if ($[11] !== className) {
    t1 = cn("text-sm font-normal text-destructive", className);
    $[11] = className;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== content || $[14] !== rest || $[15] !== t1) {
    t2 = <div role="alert" data-slot="field-error" className={t1} {...rest}>{content}</div>;
    $[13] = content;
    $[14] = rest;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function _temp2(message) {
  return <li key={message}>{message}</li>;
}
function _temp(error) {
  return error?.message;
}